import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Pagination,
    Stack,
    Typography,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import TariffTable from './TariffTable';
import tariffPdfService from '../../services/tariffPdfService';

/**
 * Tariff viewer, shared by the package and customer pages.
 *
 * A package has one sheet; a customer has one per package assigned to them, so
 * several sheets are paged rather than stacked: a customer with eight packages
 * would otherwise be an unreadable wall of tables.
 *
 * `fetcher` returns the API payload; the caller owns the endpoint so this stays
 * agnostic about which side it was opened from.
 */
const TariffDialog = ({ open, onClose, title, subtitle, filename, fetcher, emptyMessage }) => {
    const [loading, setLoading] = useState(false);
    const [sheets, setSheets] = useState([]);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);

    useEffect(() => {
        if (!open) return undefined;

        let cancelled = false;
        setLoading(true);
        setError(null);
        setPage(1);

        fetcher()
            .then((data) => {
                if (!cancelled) setSheets(data);
            })
            .catch((err) => {
                if (!cancelled) setError(err?.message || 'Failed to load the tariff');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleDownload = async () => {
        try {
            await tariffPdfService.download(sheets, { title, subtitle, filename });
        } catch (err) {
            setError('Failed to generate the PDF');
        }
    };

    const current = sheets[page - 1];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {title}
                {subtitle && (
                    <Typography variant="body2" color="text.secondary">
                        {subtitle}
                    </Typography>
                )}
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : sheets.length === 0 ? (
                    <Alert severity="info">{emptyMessage || 'Nothing to show.'}</Alert>
                ) : (
                    <>
                        {sheets.length > 1 && (
                            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Package {page} of {sheets.length}
                                </Typography>
                                <Pagination
                                    count={sheets.length}
                                    page={page}
                                    onChange={(e, value) => setPage(value)}
                                    size="small"
                                />
                            </Stack>
                        )}
                        {current && <TariffTable sheet={current} />}
                        {sheets.length > 1 && (
                            <>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="caption" color="text.secondary">
                                    The PDF contains all {sheets.length} packages, one per page.
                                </Typography>
                            </>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownload}
                    disabled={loading || sheets.length === 0}
                >
                    Download PDF
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TariffDialog;
